import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthContext } from '@/contexts/AuthContext';

export interface AppNotification {
  id: string;
  text: string;
  time: string;
  read: boolean;
  type: 'order_paid' | 'order_failed' | 'order_new' | 'info';
}

function timeAgo(dateStr: string): string {
  const secs = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (secs < 60) return 'Just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

type OrderRow = { id: string; product_name: string; user_name: string; status: string; created_at: string; amount?: number };

function toNotif(order: OrderRow, isLive = false): AppNotification {
  const label = order.status === 'paid'
    ? `Payment received — ${order.id} · ${order.product_name}`
    : order.status === 'failed'
    ? `Payment failed — ${order.id} (${order.product_name})`
    : `New order ${order.id} · ${order.product_name} by ${order.user_name}`;
  return {
    id: isLive ? `live-${Date.now()}-${order.id}` : `hist-${order.id}`,
    text: label,
    time: isLive ? 'Just now' : timeAgo(order.created_at),
    read: !isLive,
    type: order.status === 'paid' ? 'order_paid' : order.status === 'failed' ? 'order_failed' : 'order_new',
  };
}

export function useNotifications() {
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [liveToast, setLiveToast] = useState<AppNotification | null>(null);

  // Load recent orders as initial notifications (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    supabase
      .from('orders')
      .select('id, product_name, user_name, status, created_at')
      .order('created_at', { ascending: false })
      .limit(8)
      .then(({ data }) => {
        if (data) setNotifications(data.map(o => toNotif(o as OrderRow)));
      });
  }, [isAdmin]);

  // Realtime subscription for new order inserts
  useEffect(() => {
    if (!isAdmin) return;
    const channel = supabase
      .channel('orders-admin-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'orders' },
        (payload) => {
          const notif = toNotif(payload.new as OrderRow, true);
          setNotifications(prev => [notif, ...prev.slice(0, 9)]);
          setLiveToast(notif);
          setTimeout(() => setLiveToast(null), 5000);
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [isAdmin]);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  return { notifications, unreadCount, liveToast, markAllRead, isAdmin };
}
